import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined, EditOutlined } from "@ant-design/icons";

import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import {
  fetchStateConfigurations,
  updateStateConfiguration,
  type StateExperienceConfig,
  type StateExperienceConfigUpdate,
} from "./stateConfigurations";
import AppContext from "../../../context/AppContext";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import { Pagination } from "../../shared";
import { useIsDevMode } from "../../../hooks/useIsDevMode";
import { useClientCountry } from "../../../hooks/useClientCountry";
import { useIsSweepstakesApplication } from "../../../hooks/useIsSweepstakesApplication";
import { useTriveltaProduct } from "../../../hooks/useTriveltaProduct";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import { getStateNameByAbbreviation } from "../../../utils/MapStateName";
import { moneyTypeLabel } from "../../../utils/MoneyTypeLabels";
import { TRIVELTA_PRODUCTS } from "../../../utils/products";
import EditStateModal from "./EditStateModal";

import "./stateConfiguration.css";

const SEARCH_DEBOUNCE_MS = 200;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const EDIT_PERMISSION = "configurations.state_configuration";
const DEFAULT_COUNTRY = "USA";
const REBET_COIN = "rebet_coin";

type StateConfigTableAppContext = {
  adminPermissions?: string[];
};

interface TableRow {
  key: string;
  config: StateExperienceConfig;
}

function YesNoBadge({ value }: { value: boolean }) {
  return (
    <span
      className="state-config-pill"
      style={{
        background: value ? "var(--green-800)" : "var(--red-500)",
        color: "var(--white)",
      }}
    >
      {value ? AppStrings.YES : AppStrings.NO}
    </span>
  );
}

function CurrenciesCell({
  currencies,
  hideSecondaryCurrency = false,
}: {
  currencies: string[];
  hideSecondaryCurrency?: boolean;
}) {
  const visible = hideSecondaryCurrency
    ? currencies.filter((c) => c !== REBET_COIN)
    : currencies;
  if (!visible.length) {
    return (
      <span className="state-config-currencies-empty">
        {AppStrings.PAM_STATE_CONFIGURATION_CURRENCIES_EMPTY}
      </span>
    );
  }
  return (
    <span className="state-config-currencies">
      {visible.map(moneyTypeLabel).join(", ")}
    </span>
  );
}

function StateConfigurationTable() {
  const { adminPermissions } = useContext(
    AppContext,
  ) as StateConfigTableAppContext;
  const token = JSON.parse(localStorage.getItem("token") || "null");
  const client = useMemo(
    () => createPrivateAdminPanelInstance(token?.AccessToken),
    [token?.AccessToken],
  );

  // The backend hasn't shipped `configurations.state_configuration` on any
  // `/auth/me` payload yet, so gating on the permission alone leaves editing
  // dead even in dev. Mirror Sider's `hasAnyPermission(..., { developmentMode:
  // true })` so QA/engineers can exercise the modal in dev/test envs ahead of
  // the backend permission landing (AGENTS.md §10).
  const isDevEnv = useIsDevMode();
  // TCM `app_currency_config.country` — same source KPI By-State maps use
  // (`useClientCountry`) so Infinito/Estadio Gana resolve MX names, etc.
  const clientCountry = useClientCountry() ?? DEFAULT_COUNTRY;
  // Predictions product from TCM v2 — hide prediction columns/modal section
  // when the product isn't active for this client (`isActive === true` only;
  // loading/error stay hidden so we don't flash columns on outage).
  const { isActive: isPredictionsActive } = useTriveltaProduct(
    TRIVELTA_PRODUCTS.PREDICTIONS,
    undefined,
    { catalogOnly: true },
  );
  const showPrediction = isPredictionsActive === true;
  // Sweepstakes clients expose rebet_coin; non-sweepstakes hide it in the
  // currencies column + edit toggle (same gate as UserBalanceModal).
  const showSecondaryCurrency = useIsSweepstakesApplication();
  const canEdit = useMemo(
    () => isDevEnv || (adminPermissions ?? []).includes(EDIT_PERMISSION),
    [adminPermissions, isDevEnv],
  );

  // Try the client's country map first, then the US map: experiences-config
  // currently returns US state codes even on non-US clients (e.g. MEX), and
  // country maps use distinct code shapes so the fallback can't mislabel.
  const stateLabel = useCallback(
    (code: string) =>
      getStateNameByAbbreviation(code, clientCountry) ??
      getStateNameByAbbreviation(code, DEFAULT_COUNTRY) ??
      code,
    [clientCountry],
  );

  const [configs, setConfigs] = useState<StateExperienceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [editing, setEditing] = useState<StateExperienceConfig | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchStateConfigurations(client);
      setConfigs(data);
    } catch (err) {
      setLoadError(
        getApiErrorMessage(err, AppStrings.PAM_STATE_CONFIGURATION_LOAD_ERROR),
      );
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(searchQuery.trim().toLowerCase()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const rows: TableRow[] = useMemo(
    () => configs.map((config) => ({ key: config.state, config })),
    [configs],
  );

  const filteredRows = useMemo(
    () =>
      rows.filter(({ config }) => {
        if (!debouncedSearch) return true;
        const name = stateLabel(config.state);
        return (
          config.state.toLowerCase().includes(debouncedSearch) ||
          name.toLowerCase().includes(debouncedSearch)
        );
      }),
    [rows, debouncedSearch, stateLabel],
  );

  // A narrowed result set can be shorter than the current page offset, which
  // would otherwise strand the user on a page that no longer exists.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const handlePageSizeChange = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize);
    setCurrentPage(1);
  }, []);

  const handleSave = useCallback(
    async (update: StateExperienceConfigUpdate) => {
      if (!editing) return;
      setSaving(true);
      try {
        const saved = await updateStateConfiguration(
          client,
          editing.state,
          update,
        );
        setConfigs((prev) =>
          prev.map((c) => (c.state === editing.state ? saved : c)),
        );
        message.success(AppStrings.PAM_STATE_CONFIGURATION_SAVE_SUCCESS);
        setEditing(null);
      } catch (err) {
        message.error(
          getApiErrorMessage(err, AppStrings.PAM_STATE_CONFIGURATION_SAVE_ERROR),
        );
      } finally {
        setSaving(false);
      }
    },
    [client, editing],
  );

  const columns: ColumnsType<TableRow> = useMemo(() => {
    const predictionGroup: ColumnsType<TableRow>[number] = {
      title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_GROUP_PREDICTION,
      key: "prediction",
      className: "state-config-group-header state-config-partition-end",
      children: [
        {
          title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_SIGNUP_AGE,
          key: "prediction_signup_age",
          width: 120,
          render: (_: unknown, { config }: TableRow) =>
            formatString(AppStrings.PAM_STATE_CONFIGURATION_SIGNUP_AGE_VALUE, {
              age: String(config.experiences.prediction.minimum_signup_age),
            }),
        },
        {
          title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_SPORTS,
          key: "prediction_sports",
          width: 110,
          className: "state-config-cell-pill",
          render: (_: unknown, { config }: TableRow) => (
            <YesNoBadge value={config.experiences.prediction.sports} />
          ),
        },
        {
          title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_CULTURE,
          key: "prediction_culture",
          width: 110,
          className: "state-config-cell-pill",
          render: (_: unknown, { config }: TableRow) => (
            <YesNoBadge value={config.experiences.prediction.culture} />
          ),
        },
        {
          title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_RIPS,
          key: "prediction_rips",
          width: 100,
          className: "state-config-cell-pill",
          render: (_: unknown, { config }: TableRow) => (
            <YesNoBadge value={config.experiences.prediction.rips} />
          ),
        },
        {
          title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_CURRENCIES,
          key: "prediction_currencies",
          width: 160,
          className: "state-config-partition-end",
          render: (_: unknown, { config }: TableRow) => (
            <CurrenciesCell
              currencies={config.experiences.prediction.currencies}
            />
          ),
        },
      ],
    };

    return [
      {
        title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_STATE,
        key: "state",
        width: 160,
        fixed: "left",
        className: "state-config-partition-end",
        render: (_: unknown, { config }: TableRow) => stateLabel(config.state),
      },
      ...(showPrediction ? [predictionGroup] : []),
      {
        title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_GROUP_SWEEPSTAKES,
        key: "sweepstakes",
        className: "state-config-group-header",
        children: [
          {
            title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_SPORTSBOOK,
            key: "sweepstakes_sportsbook",
            width: 120,
            className: "state-config-cell-pill",
            render: (_: unknown, { config }: TableRow) => (
              <YesNoBadge value={config.experiences.sweepstakes.sportsbook} />
            ),
          },
          {
            title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_CASINO,
            key: "sweepstakes_casino",
            width: 110,
            className: "state-config-cell-pill",
            render: (_: unknown, { config }: TableRow) => (
              <YesNoBadge value={config.experiences.sweepstakes.casino} />
            ),
          },
          {
            title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_RIPS,
            key: "sweepstakes_rips",
            width: 100,
            className: "state-config-cell-pill",
            render: (_: unknown, { config }: TableRow) => (
              <YesNoBadge value={config.experiences.sweepstakes.rips} />
            ),
          },
          {
            title: AppStrings.PAM_STATE_CONFIGURATION_COLUMN_CURRENCIES,
            key: "sweepstakes_currencies",
            width: 200,
            render: (_: unknown, { config }: TableRow) => (
              <CurrenciesCell
                currencies={config.experiences.sweepstakes.currencies}
                hideSecondaryCurrency={!showSecondaryCurrency}
              />
            ),
          },
        ],
      },
      {
        // No header label — the Figma table has no "Actions" column heading,
        // just an unlabelled 48px cell with a left divider (16361:207311).
        title: null,
        key: "actions",
        width: 48,
        fixed: "right",
        align: "center" as const,
        className: "state-config-cell-actions",
        render: (_: unknown, { config }: TableRow) => (
          <button
            type="button"
            className="state-config-edit-btn"
            disabled={!canEdit}
            onClick={() => setEditing(config)}
            aria-label={formatString(
              AppStrings.PAM_STATE_CONFIGURATION_EDIT_ACTION_LABEL,
              { state: stateLabel(config.state) },
            )}
            data-testid={`state-config-edit-${config.state}`}
          >
            <EditOutlined className="state-config-edit-icon" aria-hidden />
          </button>
        ),
      },
    ];
  }, [canEdit, showPrediction, showSecondaryCurrency, stateLabel]);

  if (loading) {
    return (
      <div className="state-config-loading">
        <RebetLoader height="32px" width="32px" />
      </div>
    );
  }

  return (
    <>
      <div className="state-config-toolbar">
        <Input
          className="state-config-search"
          prefix={<SearchOutlined />}
          placeholder={AppStrings.PAM_STATE_CONFIGURATION_SEARCH_PLACEHOLDER}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          data-testid="state-config-search"
        />
      </div>

      {loadError ? (
        <div className="state-config-error" role="alert">
          <span>{loadError}</span>
          <Button onClick={() => fetchConfigs()}>{AppStrings.RETRY}</Button>
        </div>
      ) : (
        <>
          <Table<TableRow>
            className="state-config-table"
            rowKey="key"
            columns={columns}
            dataSource={paginatedRows}
            pagination={false}
            scroll={{ x: showPrediction ? 1300 : 700 }}
            locale={{
              emptyText: (
                <Empty description={AppStrings.PAM_STATE_CONFIGURATION_EMPTY} />
              ),
            }}
          />
          <div className="state-config-table-pagination">
            <Pagination
              total={filteredRows.length}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
              itemLabel={AppStrings.PAM_STATE_CONFIGURATION_PAGINATION_ITEMS}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </div>
        </>
      )}

      <EditStateModal
        open={editing !== null}
        config={editing}
        country={clientCountry}
        showPrediction={showPrediction}
        showSecondaryCurrency={showSecondaryCurrency}
        saving={saving}
        onCancel={() => setEditing(null)}
        onSave={handleSave}
      />
    </>
  );
}

export default StateConfigurationTable;
